import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { baseFormSchema, type BaseFormData } from '@/schema/template';
import {
  useUpdateDepartmentMutation,
  useUpdateTouchpointMutation,
  useUpdateSubTouchpointMutation,
  useUpdateTitleMutation,
  departmentQueryKeys,
  touchpointQueryKeys,
  subTouchpointQueryKeys,
  titleQueryKeys,
} from '@/hooks/queries/useTemplates';
import AxiosClient from '@/api/client';
import { EndpointHelpers } from '@/config/endpoints';
import type {
  ApiDepartmentDetail,
  ApiTouchpointDetail,
  ApiSubTouchpointDetail,
  ApiTitleDetail,
} from '@/types/templates';

interface EditItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  columnType: 'department' | 'touchpoint' | 'subtouchpoint' | 'title';
  itemId: string | number;
  onSuccess: () => void;
}

export function EditItemDialog({
  isOpen,
  onClose,
  columnType,
  itemId,
  onSuccess,
}: EditItemDialogProps) {
  // Fetch item details
  const { data: itemData } = useQuery({
    queryKey: (() => {
      switch (columnType) {
        case 'department':
          return departmentQueryKeys.detail(itemId);
        case 'touchpoint':
          return touchpointQueryKeys.detail(itemId);
        case 'subtouchpoint':
          return subTouchpointQueryKeys.detail(itemId);
        case 'title':
          return titleQueryKeys.detail(itemId);
      }
    })(),
    queryFn: async () => {
      const endpoint = (() => {
        switch (columnType) {
          case 'department':
            return EndpointHelpers.getDepartmentDetail(itemId);
          case 'touchpoint':
            return EndpointHelpers.getTouchpointDetail(itemId);
          case 'subtouchpoint':
            return EndpointHelpers.getSubTouchpointDetail(itemId);
          case 'title':
            return EndpointHelpers.getTitleDetail(itemId);
        }
      })();

      const client = new AxiosClient<
        undefined,
        | ApiDepartmentDetail
        | ApiTouchpointDetail
        | ApiSubTouchpointDetail
        | ApiTitleDetail
      >({
        endpoint,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch item details');
      }

      return response.data;
    },
    enabled: isOpen,
    staleTime: 60 * 1000, // Cache for 1 minute to avoid refetches
  });

  const updateDepartmentMutation = useUpdateDepartmentMutation(itemId, {
    onSuccessCallback: () => {
      onSuccess();
      onClose();
    },
  });

  const updateTouchpointMutation = useUpdateTouchpointMutation(itemId, {
    onSuccessCallback: () => {
      onSuccess();
      onClose();
    },
  });

  const updateSubTouchpointMutation = useUpdateSubTouchpointMutation(itemId, {
    onSuccessCallback: () => {
      onSuccess();
      onClose();
    },
  });

  const updateTitleMutation = useUpdateTitleMutation(itemId, {
    onSuccessCallback: () => {
      onSuccess();
      onClose();
    },
  });

  const form = useForm<BaseFormData>({
    resolver: zodResolver(baseFormSchema),
    defaultValues: {
      nameEnglish: '',
      nameArabic: '',
    },
    mode: 'onChange',
  });

  // Update form values when itemData changes
  useEffect(() => {
    if (itemData) {
      form.reset({
        nameEnglish: itemData.name_en || '',
        nameArabic: itemData.name_ar || '',
      });
    }
  }, [itemData, form]);

  const onSubmit = (data: BaseFormData) => {
    const requestData = {
      name_en: data.nameEnglish,
      name_ar: data.nameArabic,
    };

    switch (columnType) {
      case 'department':
        updateDepartmentMutation.mutate(requestData);
        break;
      case 'touchpoint':
        updateTouchpointMutation.mutate(requestData);
        break;
      case 'subtouchpoint':
        updateSubTouchpointMutation.mutate(requestData);
        break;
      case 'title':
        updateTitleMutation.mutate(requestData);
        break;
    }
  };

  const isLoading =
    updateDepartmentMutation.isPending ||
    updateTouchpointMutation.isPending ||
    updateSubTouchpointMutation.isPending ||
    updateTitleMutation.isPending;

  const handleClose = () => {
    if (!isLoading) {
      form.reset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit {columnType}</DialogTitle>
          <DialogDescription>
            Update the English and Arabic names for this {columnType}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nameEnglish">English Name *</Label>
              <Controller
                name="nameEnglish"
                control={form.control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="nameEnglish"
                    placeholder={`Enter ${columnType} name in English`}
                    className={
                      form.formState.errors.nameEnglish ? 'border-red-500' : ''
                    }
                    disabled={isLoading}
                  />
                )}
              />
              {form.formState.errors.nameEnglish && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.nameEnglish.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="nameArabic">Arabic Name *</Label>
              <Controller
                name="nameArabic"
                control={form.control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="nameArabic"
                    placeholder={`أدخل اسم ${columnType} بالعربية`}
                    className={`text-right ${form.formState.errors.nameArabic ? 'border-red-500' : ''}`}
                    dir="rtl"
                    disabled={isLoading}
                  />
                )}
              />
              {form.formState.errors.nameArabic && (
                <p className="text-sm text-red-500 text-right">
                  {form.formState.errors.nameArabic.message}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!form.formState.isValid || isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
